(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cleanups = new WeakMap();
  const html = document.documentElement;
  let revealObserver = null;

  function all(root, selector) {
    const elements = Array.from(root.querySelectorAll?.(selector) || []);
    if (root instanceof Element && root.matches(selector)) elements.unshift(root);
    return elements;
  }

  function addCleanup(element, callback) {
    const callbacks = cleanups.get(element) || [];
    callbacks.push(callback);
    cleanups.set(element, callbacks);
  }

  function cleanup(root) {
    all(root, '[data-wot-observed="true"]').forEach((element) => {
      revealObserver?.unobserve(element);
      delete element.dataset.wotObserved;
    });

    all(root, '[data-wot-ready="true"]').forEach((element) => {
      (cleanups.get(element) || []).forEach((callback) => callback());
      cleanups.delete(element);
      delete element.dataset.wotReady;
    });
  }

  function selectedMotionStrength() {
    const selected = document.querySelector('[data-wot-motion-strength]')?.dataset.wotMotionStrength;
    return ['off', 'subtle', 'medium'].includes(selected) ? selected : 'subtle';
  }

  function motionAllowed() {
    return !reducedMotion.matches && html.dataset.wotMotion !== 'off';
  }

  function componentMotionAllowed(element) {
    return element?.dataset.wotForceMotion === 'true' || motionAllowed();
  }

  function applyMotionSetting() {
    const selected = selectedMotionStrength();
    const effective = reducedMotion.matches ? 'off' : selected;
    html.dataset.wotMotionPreference = selected;
    html.dataset.wotMotion = effective;
    html.classList.toggle('wot-motion-ready', effective !== 'off' && 'IntersectionObserver' in window);

    if (effective === 'off') {
      all(document, '[data-wot-reveal], [data-wot-hero]').forEach((element) => element.classList.add('is-visible'));
      all(document, '[data-wot-process]').forEach((element) => element.style.setProperty('--wot-process-progress', '1'));
    }
  }

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -9% 0px', threshold: 0.06 });
    return revealObserver;
  }

  function observeOnce(element) {
    if (element.dataset.wotObserved === 'true') return;
    element.dataset.wotObserved = 'true';

    const observer = getRevealObserver();
    if (!motionAllowed() || !observer) {
      element.classList.add('is-visible');
      return;
    }

    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.95 && rect.bottom > 0) {
      element.classList.add('is-visible');
      return;
    }

    observer.observe(element);
  }

  function initHero(hero) {
    if (hero.dataset.wotReady === 'true') return;
    hero.dataset.wotReady = 'true';
    let firstFrame = 0;
    let secondFrame = 0;
    let fallbackTimer = 0;

    const reveal = () => hero.classList.add('is-visible');
    if (hero.hasAttribute('data-wot-hero-animate') && motionAllowed()) {
      hero.classList.add('is-motion-ready');
      firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(reveal);
      });
      fallbackTimer = window.setTimeout(reveal, 1200);
    } else {
      reveal();
    }

    addCleanup(hero, () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      clearTimeout(fallbackTimer);
    });
  }

  function initProcess(process) {
    if (process.dataset.wotReady === 'true') return;
    process.dataset.wotReady = 'true';

    const controller = new AbortController();
    const signal = controller.signal;
    const progressLine = process.querySelector('[data-wot-process-progress]');
    const steps = all(process, '[data-wot-process-step]');
    const markers = steps.map((step) => step.querySelector('[data-wot-process-marker]') || step);
    const flowEnabled = process.dataset.wotProcessFlow !== 'false';
    const mobileAllowed = process.dataset.wotProcessMobile !== 'false';
    const segmentDuration = Math.max(700, Math.min(1800, Number(process.dataset.wotProcessDuration || 1000)));

    let observer = null;
    let frame = 0;
    let pulseTimers = [];
    let started = false;
    let finished = false;
    let geometry = {
      start: 0,
      end: 1,
      range: 1,
      checkpoints: [],
      height: 1
    };

    function clamp(value, min = 0, max = 1) {
      return Math.min(max, Math.max(min, value));
    }

    function clearPulse(step) {
      step.classList.remove('is-pulse');
    }

    function pulseStep(index) {
      const step = steps[index];
      if (!step) return;
      clearPulse(step);
      // restart animation reliably
      void step.offsetWidth;
      step.classList.add('is-pulse');
      const timer = window.setTimeout(() => clearPulse(step), 420);
      pulseTimers.push(timer);
    }

    function updateGeometry() {
      const pageOffset = window.scrollY || window.pageYOffset;
      const processRect = process.getBoundingClientRect();
      const processTop = processRect.top + pageOffset;
      const processHeight = Math.max(process.offsetHeight, 1);
      const checkpoints = markers.map((marker) => {
        const rect = marker.getBoundingClientRect();
        return (rect.top + pageOffset + (rect.height * 0.5)) - processTop;
      });

      let startPoint = checkpoints[0] || 0;
      let endPoint = checkpoints[checkpoints.length - 1] || processHeight;
      if (endPoint <= startPoint) endPoint = startPoint + 1;

      geometry = {
        start: startPoint,
        end: endPoint,
        range: Math.max(1, endPoint - startPoint),
        checkpoints,
        height: processHeight
      };

      process.style.setProperty('--wot-process-rail-start', `${geometry.start.toFixed(2)}px`);
      process.style.setProperty('--wot-process-rail-end', `${Math.max(0, geometry.height - geometry.end).toFixed(2)}px`);
    }

    function setStates(completeCount, activeIndex) {
      steps.forEach((step, index) => {
        const isComplete = index < completeCount;
        const isActive = index === activeIndex;
        step.classList.toggle('is-complete', isComplete);
        step.classList.toggle('is-active', isActive);
        step.classList.toggle('is-upcoming', !isComplete && !isActive);
      });
      process.classList.toggle('is-process-started', completeCount > 0);
      process.classList.toggle('is-process-complete', completeCount >= steps.length);
    }

    function renderTravel(travelPx, completeCount, activeIndex) {
      const ratio = geometry.range > 0 ? clamp((travelPx - geometry.start) / geometry.range) : 0;
      const lineHeight = Math.max(0, travelPx - geometry.start);
      process.style.setProperty('--wot-process-progress', ratio.toFixed(4));
      process.style.setProperty('--wot-process-travel', `${travelPx.toFixed(2)}px`);
      if (progressLine) progressLine.style.height = `${lineHeight.toFixed(2)}px`;
      setStates(completeCount, activeIndex);
    }

    function resetStates() {
      pulseTimers.forEach((timer) => window.clearTimeout(timer));
      pulseTimers = [];
      steps.forEach((step) => {
        step.classList.remove('is-complete', 'is-active', 'is-upcoming', 'is-pulse');
        step.classList.add('is-upcoming');
      });
      renderTravel(geometry.start, 0, -1);
      process.classList.remove('is-process-started', 'is-process-complete', 'is-process-enhanced');
    }

    function finish() {
      finished = true;
      renderTravel(geometry.end, steps.length, -1);
      process.classList.add('is-process-complete');
    }

    function playSegments() {
      const segmentCount = Math.max(geometry.checkpoints.length - 1, 0);
      if (segmentCount === 0) {
        finish();
        return;
      }

      let segmentIndex = 0;
      let segmentStartTime = 0;
      let currentSegmentStart = geometry.checkpoints[0];
      let currentSegmentEnd = geometry.checkpoints[1];

      setStates(1, -1);
      renderTravel(currentSegmentStart, 1, -1);
      pulseStep(0);

      function tick(now) {
        if (!segmentStartTime) segmentStartTime = now;
        const raw = clamp((now - segmentStartTime) / segmentDuration);
        const eased = raw;
        const currentTravel = currentSegmentStart + ((currentSegmentEnd - currentSegmentStart) * eased);
        renderTravel(currentTravel, segmentIndex + 1, -1);

        if (raw < 1) {
          frame = requestAnimationFrame(tick);
          return;
        }

        const reachedStepIndex = segmentIndex + 1;
        renderTravel(currentSegmentEnd, reachedStepIndex + 1, -1);
        pulseStep(reachedStepIndex);
        segmentIndex += 1;

        if (segmentIndex >= segmentCount) {
          frame = 0;
          finish();
          return;
        }

        currentSegmentStart = geometry.checkpoints[segmentIndex];
        currentSegmentEnd = geometry.checkpoints[segmentIndex + 1];
        segmentStartTime = 0;
        frame = requestAnimationFrame(tick);
      }

      frame = requestAnimationFrame(tick);
    }

    function play() {
      if (started || finished) return;
      started = true;
      observer?.disconnect();
      updateGeometry();
      resetStates();

      if (!flowEnabled || (window.innerWidth < 750 && !mobileAllowed) || !componentMotionAllowed(process)) {
        setStates(steps.length, -1);
        renderTravel(geometry.end, steps.length, -1);
        finish();
        return;
      }

      process.classList.add('is-process-enhanced');
      playSegments();
    }

    function prepare() {
      updateGeometry();
      if (!started && !finished) {
        renderTravel(geometry.start, 0, -1);
      }
    }

    prepare();

    const shopifySection = process.closest('.shopify-section');
    const isThemeEditor = !!window.Shopify?.designMode;

    function replayProcess() {
      cancelAnimationFrame(frame);
      frame = 0;
      started = false;
      finished = false;
      updateGeometry();
      resetStates();
      requestAnimationFrame(() => requestAnimationFrame(play));
    }

    if (isThemeEditor) {
      window.setTimeout(replayProcess, 80);
    }

    if (!flowEnabled || !('IntersectionObserver' in window) || isThemeEditor) {
      play();
    } else {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.18)) {
          play();
        }
      }, {
        threshold: [0.18],
        rootMargin: '0px 0px -8% 0px'
      });
      observer.observe(process);

      const rect = process.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.88 && rect.bottom > window.innerHeight * 0.12) {
        play();
      }
    }

    window.addEventListener('resize', prepare, { passive: true, signal });
    window.addEventListener('orientationchange', prepare, { passive: true, signal });

    process.addEventListener('wot:replay-process', replayProcess, { signal });

    if (isThemeEditor) {
      const replayIfSelected = (event) => {
        if (shopifySection && event.target === shopifySection) replayProcess();
      };

      document.addEventListener('shopify:section:select', replayIfSelected, { signal });
      document.addEventListener('shopify:section:load', replayIfSelected, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        if (shopifySection && shopifySection.contains(event.target)) replayProcess();
      }, { signal });
    }

    addCleanup(process, () => {
      controller.abort();
      observer?.disconnect();
      cancelAnimationFrame(frame);
      pulseTimers.forEach((timer) => window.clearTimeout(timer));
      pulseTimers = [];
      process.classList.remove('is-process-enhanced', 'is-process-started', 'is-process-complete');
      steps.forEach((step) => step.classList.remove('is-complete', 'is-active', 'is-upcoming', 'is-pulse'));
      process.style.removeProperty('--wot-process-progress');
      process.style.removeProperty('--wot-process-travel');
      process.style.removeProperty('--wot-process-rail-start');
      process.style.removeProperty('--wot-process-rail-end');
      if (progressLine) progressLine.style.removeProperty('height');
    });
  }

  function initCounterGroup(group) {
    if (group.dataset.wotReady === 'true') return;
    group.dataset.wotReady = 'true';

    const counters = all(group, '[data-wot-counter]');
    if (counters.length === 0) return;

    const controller = new AbortController();
    const signal = controller.signal;
    const frames = new Set();
    let observer = null;
    let started = false;

    const formatter = new Intl.NumberFormat('de-DE', {
      maximumFractionDigits: 0,
      useGrouping: true,
    });

    function finalText(counter) {
      const target = Number(counter.dataset.wotCounterTarget || 0);
      const prefix = counter.dataset.wotCounterPrefix || '';
      const suffix = counter.dataset.wotCounterSuffix || '';
      return `${prefix}${formatter.format(target)}${suffix}`;
    }

    function setFinalValues() {
      counters.forEach((counter) => {
        const output = counter.querySelector('[data-wot-counter-output]');
        if (output) output.textContent = finalText(counter);
        counter.dataset.wotCounterComplete = 'true';
      });
    }

    function startCounters() {
      if (started) return;
      started = true;
      observer?.disconnect();

      if (!componentMotionAllowed(group)) {
        setFinalValues();
        return;
      }

      const sharedStart = performance.now();

      counters.forEach((counter) => {
        if (counter.dataset.wotCounterComplete === 'true') return;

        const output = counter.querySelector('[data-wot-counter-output]');
        if (!output) return;

        const target = Math.max(0, Number(counter.dataset.wotCounterTarget || 0));
        const duration = Math.max(250, Number(counter.dataset.wotCounterDuration || 1500));
        const prefix = counter.dataset.wotCounterPrefix || '';
        const suffix = counter.dataset.wotCounterSuffix || '';

        output.textContent = `${prefix}0`;

        function tick(now) {
          if (!componentMotionAllowed(group)) {
            output.textContent = `${prefix}${formatter.format(target)}${suffix}`;
            counter.dataset.wotCounterComplete = 'true';
            return;
          }

          const progress = Math.min((now - sharedStart) / duration, 1);
          const easedProgress = 1 - Math.pow(1 - progress, 3);
          const currentValue = Math.round(target * easedProgress);
          const isComplete = progress >= 1;

          output.textContent = `${prefix}${formatter.format(currentValue)}${isComplete ? suffix : ''}`;

          if (isComplete) {
            counter.dataset.wotCounterComplete = 'true';
            return;
          }

          const nextFrame = requestAnimationFrame(tick);
          frames.add(nextFrame);
        }

        const firstFrame = requestAnimationFrame(tick);
        frames.add(firstFrame);
      });
    }

    counters.forEach((counter) => {
      counter.dataset.wotCounterInitialized = 'true';
      const output = counter.querySelector('[data-wot-counter-output]');
      if (output && componentMotionAllowed(group)) output.textContent = `${counter.dataset.wotCounterPrefix || ''}0`;
    });

    if (!componentMotionAllowed(group) || !('IntersectionObserver' in window)) {
      startCounters();
    } else {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.3)) {
          startCounters();
        }
      }, { threshold: [0.3], rootMargin: '0px 0px -4% 0px' });
      observer.observe(group);

      const rect = group.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9 && rect.bottom > window.innerHeight * 0.1) {
        startCounters();
      }
    }

    reducedMotion.addEventListener?.('change', () => {
      if (reducedMotion.matches && group.dataset.wotForceMotion !== 'true') setFinalValues();
    }, { signal });

    addCleanup(group, () => {
      controller.abort();
      observer?.disconnect();
      frames.forEach((frameId) => cancelAnimationFrame(frameId));
      counters.forEach((counter) => {
        delete counter.dataset.wotCounterInitialized;
        delete counter.dataset.wotCounterComplete;
        const output = counter.querySelector('[data-wot-counter-output]');
        if (output) output.textContent = finalText(counter);
      });
    });
  }

  function initChaosChat(chat) {
    if (chat.dataset.wotReady === 'true') return;
    chat.dataset.wotReady = 'true';

    const viewport = chat.querySelector('[data-wot-chat-viewport]');
    const messages = all(chat, '[data-wot-chat-message]');
    const typing = chat.querySelector('[data-wot-chat-typing-indicator]');
    const typingAvatar = chat.querySelector('[data-wot-chat-typing-avatar]');
    const typingName = chat.querySelector('[data-wot-chat-typing-name]');

    if (!viewport || messages.length === 0) return;

    const controller = new AbortController();
    const signal = controller.signal;
    const timeouts = new Set();
    let observer = null;
    let cancelled = false;
    let hasPlayed = false;

    const speedMultiplier = {
      slow: 1.25,
      normal: 1,
      fast: 0.72,
    }[chat.dataset.wotChatSpeed] || 1;

    function wait(ms) {
      return new Promise((resolve) => {
        const id = window.setTimeout(() => {
          timeouts.delete(id);
          resolve();
        }, Math.max(0, ms * speedMultiplier));
        timeouts.add(id);
      });
    }

    function scrollToBottom(behavior = 'auto') {
      requestAnimationFrame(() => {
        viewport.scrollTo({
          top: viewport.scrollHeight,
          behavior: componentMotionAllowed(chat) ? behavior : 'auto',
        });
      });
    }

    function showAll() {
      messages.forEach((message) => {
        message.hidden = false;
        message.removeAttribute('aria-hidden');
        message.classList.remove('is-chat-entering');
      });
      if (typing) {
        typing.hidden = true;
        typing.setAttribute('aria-hidden', 'true');
      }
      scrollToBottom('auto');
    }

    function resetMessages() {
      messages.forEach((message) => {
        message.hidden = true;
        message.setAttribute('aria-hidden', 'true');
        message.classList.remove('is-chat-entering');
      });
      if (typing) {
        typing.hidden = true;
        typing.setAttribute('aria-hidden', 'true');
      }
      viewport.scrollTop = 0;
    }

    async function play() {
      if (cancelled || hasPlayed) return;
      hasPlayed = true;
      chat.classList.add('is-chat-enhanced');
      resetMessages();

      for (const message of messages) {
        if (cancelled) return;

        const isMe = message.dataset.wotChatIsMe === 'true';
        const typingMs = Number(message.dataset.wotChatTyping || 0);

        if (!isMe && typing && typingMs > 0) {
          typing.hidden = false;
          typing.removeAttribute('aria-hidden');
          if (typingAvatar) {
            typingAvatar.textContent = message.dataset.wotChatInitials || '';
            typingAvatar.style.setProperty('--wot-chat-avatar', message.dataset.wotChatColor || '#5ac8fa');
          }
          if (typingName) typingName.textContent = message.dataset.wotChatName || '';
          scrollToBottom('smooth');
          await wait(typingMs);
          if (cancelled) return;
          typing.hidden = true;
          typing.setAttribute('aria-hidden', 'true');
        }

        message.hidden = false;
        message.removeAttribute('aria-hidden');
        message.classList.remove('is-chat-entering');
        void message.offsetWidth;
        message.classList.add('is-chat-entering');
        scrollToBottom('smooth');

        await wait(Number(message.dataset.wotChatDelay || 500));
      }

      if (chat.dataset.wotChatRepeat === 'true' && !cancelled) {
        await wait(3500);
        if (cancelled) return;
        hasPlayed = false;
        play();
      }
    }

    function startWhenVisible() {
      const animate = chat.dataset.wotChatAnimate === 'true' && componentMotionAllowed(chat);

      if (!animate) {
        showAll();
        return;
      }

      // Hide the final state as soon as JavaScript has initialized. Without this,
      // visitors can see the complete conversation before the observer starts it.
      chat.classList.add('is-chat-enhanced');
      resetMessages();

      const startPlayback = () => {
        observer?.disconnect();
        // A brief delay makes the beginning of the live sequence clearly visible.
        const id = window.setTimeout(() => {
          timeouts.delete(id);
          play();
        }, 280);
        timeouts.add(id);
      };

      if (!('IntersectionObserver' in window)) {
        startPlayback();
        return;
      }

      observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35)) return;
        startPlayback();
      }, { threshold: [0.35], rootMargin: '0px 0px -3% 0px' });

      observer.observe(chat);

      const rect = chat.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.82 && rect.bottom > window.innerHeight * 0.18) {
        startPlayback();
      }
    }

    chat.addEventListener('wot:show-all-chat', () => {
      cancelled = true;
      timeouts.forEach((id) => clearTimeout(id));
      timeouts.clear();
      showAll();
    }, { signal });

    reducedMotion.addEventListener?.('change', () => {
      if (!reducedMotion.matches || chat.dataset.wotForceMotion === 'true') return;
      cancelled = true;
      timeouts.forEach((id) => clearTimeout(id));
      timeouts.clear();
      showAll();
    }, { signal });

    startWhenVisible();

    addCleanup(chat, () => {
      cancelled = true;
      controller.abort();
      observer?.disconnect();
      timeouts.forEach((id) => clearTimeout(id));
      timeouts.clear();
      chat.classList.remove('is-chat-enhanced');
      showAll();
    });
  }

  function initSlider(slider) {
    if (slider.dataset.wotReady === 'true') return;
    const track = slider.querySelector('[data-wot-slider-track]');
    const slides = all(slider, '[data-wot-slide]');
    if (!track || slides.length === 0) return;

    slider.dataset.wotReady = 'true';
    const previous = slider.querySelector('[data-wot-slider-prev]');
    const next = slider.querySelector('[data-wot-slider-next]');
    const current = slider.querySelector('[data-wot-slider-current]');
    const progress = slider.querySelector('[data-wot-slider-progress]');
    const controller = new AbortController();
    const signal = controller.signal;
    let index = 0;
    let frame = 0;

    function update(nextIndex) {
      index = Math.max(0, Math.min(nextIndex, slides.length - 1));
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (current) current.textContent = String(index + 1).padStart(2, '0');
      if (progress) progress.style.setProperty('--wot-slider-progress', String((index + 1) / slides.length));
    }

    function goTo(nextIndex, behavior = motionAllowed() ? 'smooth' : 'auto') {
      const wrapped = (nextIndex + slides.length) % slides.length;
      const targetSlide = slides[wrapped];
      const left = targetSlide.offsetLeft - Number.parseFloat(getComputedStyle(track).paddingLeft || '0');
      track.scrollTo({ left, behavior });
      update(wrapped);
    }

    function findNearest() {
      frame = 0;
      const center = track.scrollLeft + (track.clientWidth / 2);
      let nearestIndex = 0;
      let nearestDistance = Infinity;
      slides.forEach((slide, slideIndex) => {
        const distance = Math.abs(center - (slide.offsetLeft + (slide.offsetWidth / 2)));
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = slideIndex;
        }
      });
      update(nearestIndex);
    }

    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(findNearest);
    }, { passive: true, signal });
    track.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      goTo(index + (event.key === 'ArrowRight' ? 1 : -1));
    }, { signal });
    previous?.addEventListener('click', () => goTo(index - 1), { signal });
    next?.addEventListener('click', () => goTo(index + 1), { signal });
    slider.addEventListener('wot:go-to-slide', (event) => goTo(event.detail.index), { signal });
    update(0);

    addCleanup(slider, () => {
      controller.abort();
      cancelAnimationFrame(frame);
    });
  }

  function initReviews(carousel) {
    if (carousel.dataset.wotReady === 'true') return;
    const viewport = carousel.querySelector('[data-wot-reviews-viewport]');
    const track = carousel.querySelector('[data-wot-reviews-track]');
    const firstGroup = track?.querySelector('.wot-home-reviews__group');
    if (!viewport || !track || !firstGroup) return;

    carousel.dataset.wotReady = 'true';
    const previous = carousel.querySelector('[data-wot-reviews-prev]');
    const next = carousel.querySelector('[data-wot-reviews-next]');
    const toggle = carousel.querySelector('[data-wot-reviews-toggle]');
    const toggleLabel = carousel.querySelector('[data-wot-reviews-toggle-label]');
    const controller = new AbortController();
    const signal = controller.signal;
    let frame = 0;
    let lastTime = 0;
    let inView = false;
    let visibilityFrame = 0;
    let hovering = false;
    let focused = false;
    let pointerDown = false;
    let pausedByUser = false;
    let position = viewport.scrollLeft;

    function autoEnabled() {
      return carousel.dataset.wotReviewsAuto === 'true' && motionAllowed();
    }

    function paused() {
      return pausedByUser || hovering || focused || pointerDown || document.hidden || !inView || !autoEnabled();
    }

    function syncMode() {
      const enabled = autoEnabled();
      carousel.classList.toggle('is-auto-enabled', enabled);
      if (toggle) toggle.hidden = !enabled;
      if (!enabled || paused()) {
        cancelAnimationFrame(frame);
        frame = 0;
        if (!enabled) {
          viewport.scrollLeft = Math.min(viewport.scrollLeft, firstGroup.scrollWidth);
          position = viewport.scrollLeft;
        }
      } else if (!frame) {
        lastTime = performance.now();
        frame = requestAnimationFrame(tick);
      }
    }

    function syncToggle() {
      toggle?.setAttribute('aria-pressed', pausedByUser ? 'true' : 'false');
      if (toggleLabel) {
        toggleLabel.textContent = pausedByUser
          ? carousel.dataset.wotReviewsPlayLabel
          : carousel.dataset.wotReviewsPauseLabel;
      }
    }

    function tick(now) {
      frame = 0;
      const delta = Math.min(34, now - lastTime || 16.7);
      lastTime = now;
      if (!paused()) {
        position += delta * 0.018;
        const resetAt = firstGroup.scrollWidth;
        if (resetAt > 0 && position >= resetAt) position -= resetAt;
        viewport.scrollLeft = position;
      }
      if (!paused()) frame = requestAnimationFrame(tick);
    }

    function move(direction) {
      const card = firstGroup.querySelector('.wot-home-reviews__card');
      const amount = (card?.getBoundingClientRect().width || viewport.clientWidth * 0.85) + 16;
      position = viewport.scrollLeft + (amount * direction);
      viewport.scrollBy({ left: amount * direction, behavior: motionAllowed() ? 'smooth' : 'auto' });
    }

    function updateVisibility() {
      visibilityFrame = 0;
      const rect = carousel.getBoundingClientRect();
      inView = rect.top < window.innerHeight + 160 && rect.bottom > -160;
      syncMode();
    }

    function scheduleVisibility() {
      if (!visibilityFrame) visibilityFrame = requestAnimationFrame(updateVisibility);
    }

    previous?.addEventListener('click', () => move(-1), { signal });
    next?.addEventListener('click', () => move(1), { signal });
    toggle?.addEventListener('click', () => {
      pausedByUser = !pausedByUser;
      syncToggle();
      syncMode();
    }, { signal });
    carousel.addEventListener('mouseenter', () => { hovering = true; syncMode(); }, { signal });
    carousel.addEventListener('mouseleave', () => { hovering = false; syncMode(); }, { signal });
    carousel.addEventListener('focusin', () => { focused = true; syncMode(); }, { signal });
    carousel.addEventListener('focusout', (event) => {
      focused = carousel.contains(event.relatedTarget);
      syncMode();
    }, { signal });
    viewport.addEventListener('pointerdown', () => { pointerDown = true; syncMode(); }, { signal });
    window.addEventListener('pointerup', () => { pointerDown = false; syncMode(); }, { signal });
    viewport.addEventListener('scroll', () => {
      if (pointerDown || pausedByUser || !autoEnabled()) position = viewport.scrollLeft;
    }, { passive: true, signal });
    document.addEventListener('visibilitychange', syncMode, { signal });
    reducedMotion.addEventListener?.('change', syncMode, { signal });
    window.addEventListener('scroll', scheduleVisibility, { passive: true, signal });
    window.addEventListener('resize', scheduleVisibility, { passive: true, signal });

    syncToggle();
    updateVisibility();
    addCleanup(carousel, () => {
      controller.abort();
      cancelAnimationFrame(frame);
      cancelAnimationFrame(visibilityFrame);
    });
  }


  function initFaqStudio(faq) {
    if (faq.dataset.wotReady === 'true') return;
    faq.dataset.wotReady = 'true';
    const controller = new AbortController();
    const signal = controller.signal;
    const tabs = all(faq, '[data-faq-tab]');
    const panels = all(faq, '[data-faq-featured]');
    const rows = all(faq, '[data-faq-row]');
    let activeIndex = Math.max(0, Math.min(Number(faq.dataset.initialIndex) || 0, tabs.length - 1));

    function closeRows(except = null) {
      rows.forEach((row) => {
        if (row === except) return;
        row.dataset.open = 'false';
        const button = row.querySelector('[data-faq-extra-toggle]');
        const panel = row.querySelector('[data-faq-extra-panel]');
        button?.setAttribute('aria-expanded', 'false');
        panel?.setAttribute('aria-hidden', 'true');
      });
    }

    function revealActivePanel(index) {
      panels.forEach((panel) => {
        const isActive = Number(panel.dataset.index) === index;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
        panel.classList.remove('is-entering');
        if (isActive && componentMotionAllowed(faq)) {
          void panel.offsetWidth;
          panel.classList.add('is-entering');
        }
      });
    }

    function setActive(index, focusTab = false) {
      if (!tabs.length) return;
      activeIndex = (index + tabs.length) % tabs.length;
      faq.dataset.activeIndex = String(activeIndex);

      tabs.forEach((tab) => {
        const isActive = Number(tab.dataset.index) === activeIndex;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
        if (isActive && focusTab) tab.focus({ preventScroll: true });
      });

      revealActivePanel(activeIndex);
      closeRows();
      rows.forEach((row) => {
        row.hidden = Number(row.dataset.index) === activeIndex;
      });

      const activeTab = tabs.find((tab) => Number(tab.dataset.index) === activeIndex);
      if (activeTab && window.innerWidth < 900) {
        activeTab.scrollIntoView({ behavior: componentMotionAllowed(faq) ? 'smooth' : 'auto', block: 'nearest', inline: 'center' });
      }
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => setActive(Number(tab.dataset.index)), { signal });
      tab.addEventListener('keydown', (event) => {
        let target = null;
        if (['ArrowDown', 'ArrowRight'].includes(event.key)) target = activeIndex + 1;
        if (['ArrowUp', 'ArrowLeft'].includes(event.key)) target = activeIndex - 1;
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = tabs.length - 1;
        if (target === null) return;
        event.preventDefault();
        setActive(target, true);
      }, { signal });
    });

    rows.forEach((row) => {
      const button = row.querySelector('[data-faq-extra-toggle]');
      const panel = row.querySelector('[data-faq-extra-panel]');
      if (!button || !panel) return;
      button.addEventListener('click', () => {
        const willOpen = row.dataset.open !== 'true';
        closeRows(row);
        row.dataset.open = willOpen ? 'true' : 'false';
        button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        panel.setAttribute('aria-hidden', willOpen ? 'false' : 'true');
      }, { signal });
    });

    faq.addEventListener('wot:faq-select', (event) => {
      const index = Number(event.detail?.index);
      if (Number.isFinite(index)) setActive(index);
    }, { signal });

    setActive(activeIndex);
    addCleanup(faq, () => controller.abort());
  }

  function initAccordion(accordion) {
    if (accordion.dataset.wotReady === 'true') return;
    accordion.dataset.wotReady = 'true';
    const controller = new AbortController();
    const signal = controller.signal;

    all(accordion, 'details').forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;
      const sync = () => summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      sync();
      details.addEventListener('toggle', sync, { signal });
    });

    addCleanup(accordion, () => controller.abort());
  }

  function initStickyCta(sticky) {
    if (sticky.dataset.wotReady === 'true') return;
    sticky.dataset.wotReady = 'true';
    const controller = new AbortController();
    const signal = controller.signal;
    const hero = document.querySelector('[data-wot-hero]');
    const contact = document.getElementById('kontakt');
    const footer = document.querySelector('footer.footer');
    const menuDrawer = document.getElementById('MenuDrawer');
    let frame = 0;

    function visible(element) {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    }

    function menuOpen() {
      return document.body.classList.contains('has-modal-open')
        || document.body.classList.contains('has-modal-opening')
        || Boolean(document.querySelector('#MenuDrawer[open], [aria-controls="MenuDrawer"][aria-expanded="true"]'));
    }

    function update() {
      frame = 0;
      const show = (!hero || hero.getBoundingClientRect().bottom < 40) && !visible(contact) && !visible(footer) && !menuOpen();
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    document.addEventListener('toggle', schedule, { capture: true, signal });
    const bodyObserver = new MutationObserver(schedule);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    if (menuDrawer) bodyObserver.observe(menuDrawer, { attributes: true, attributeFilter: ['open', 'hidden'] });
    update();

    addCleanup(sticky, () => {
      controller.abort();
      bodyObserver.disconnect();
      cancelAnimationFrame(frame);
    });
  }

  function init(root = document) {
    applyMotionSetting();

    const safely = (elements, initializer) => {
      elements.forEach((element) => {
        try {
          initializer(element);
        } catch (_) {
          delete element.dataset.wotReady;
        }
      });
    };

    safely(all(root, '[data-wot-hero]'), initHero);
    all(root, '[data-wot-reveal]').forEach(observeOnce);
    safely(all(root, '[data-wot-process]'), initProcess);
    safely(all(root, '[data-wot-counter-group]'), initCounterGroup);
    safely(all(root, '[data-wot-chaos-chat]'), initChaosChat);
    safely(all(root, '[data-wot-slider]'), initSlider);
    safely(all(root, '[data-wot-reviews]'), initReviews);
    safely(all(root, '[data-wot-faq-studio]'), initFaqStudio);
    safely(all(root, '[data-wot-accordion]'), initAccordion);
    safely(all(root, '[data-wot-sticky-cta]'), initStickyCta);
  }

  function start() {
    init(document);
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      cleanup(event.target);
      applyMotionSetting();
    });
    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      if (!(block instanceof Element)) return;
      const faqTabWrap = block.closest('[data-faq-tab-wrap]');
      const faqTab = faqTabWrap?.querySelector('[data-faq-tab]');
      const faqStudio = block.closest('[data-wot-faq-studio]');
      if (faqTab && faqStudio) {
        faqStudio.dispatchEvent(new CustomEvent('wot:faq-select', { detail: { index: Number(faqTab.dataset.index) } }));
        faqTab.scrollIntoView({ behavior: motionAllowed() ? 'smooth' : 'auto', block: 'center', inline: 'center' });
        return;
      }

      const chaosChat = block.closest('[data-wot-chaos-chat]');
      if (chaosChat) chaosChat.dispatchEvent(new CustomEvent('wot:show-all-chat'));

      const slide = block.closest('[data-wot-slide]');
      const slider = block.closest('[data-wot-slider]');
      if (slide && slider) {
        const slides = all(slider, '[data-wot-slide]');
        slider.dispatchEvent(new CustomEvent('wot:go-to-slide', { detail: { index: slides.indexOf(slide) } }));
      } else {
        block.scrollIntoView({ behavior: motionAllowed() ? 'smooth' : 'auto', block: 'center' });
      }
    });
    reducedMotion.addEventListener?.('change', applyMotionSetting);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
